import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';

interface Venue {
  id: string;
  name: string;
  pricePerHour: number;
}

interface Booking {
  id: string;
  boardId: string;
  venueId: string;
  userId: string;
  userName: string;
  startTime: Timestamp;
  endTime: Timestamp;
  status: 'confirmed' | 'cancelled' | 'completed';
}

interface VenueBookingStatisticsProps {
  venues: Venue[];
}

const VenueBookingStatistics: React.FC<VenueBookingStatisticsProps> = ({ venues }) => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  useEffect(() => {
    if (venues.length > 0) {
      loadBookings();
    }
  }, [timeRange, venues]);

  const getDateRange = () => {
    const now = new Date();
    let start: Date;
    let end: Date = new Date(now);

    switch (timeRange) {
      case 'daily':
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'weekly':
        start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'monthly':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      default:
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  };

  const loadBookings = async () => {
    if (venues.length === 0) return;

    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const venueIds = venues.map(v => v.id);

      // Firestore 'in' query limit is 10, so we need to batch if more venues
      const batchSize = 10;
      const allBookings: Booking[] = [];

      for (let i = 0; i < venueIds.length; i += batchSize) {
        const batchVenueIds = venueIds.slice(i, i + batchSize);

        const q = query(
          collection(db, 'bookings'),
          where('venueId', 'in', batchVenueIds),
          where('startTime', '>=', Timestamp.fromDate(start)),
          where('startTime', '<=', Timestamp.fromDate(end)),
          where('status', 'in', ['confirmed', 'completed'])
        );

        const querySnapshot = await getDocs(q);

        const bookingPromises = querySnapshot.docs.map(async (docSnapshot) => {
          const bookingData = { id: docSnapshot.id, ...docSnapshot.data() } as Booking;

          if (!bookingData.userName && bookingData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                bookingData.userName = userData.username || userData.displayName || userData.email || 'Ismeretlen felhasználó';
              } else {
                bookingData.userName = 'Ismeretlen felhasználó';
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
              bookingData.userName = 'Ismeretlen felhasználó';
            }
          }

          return bookingData;
        });

        const resolvedBookings = await Promise.all(bookingPromises);
        allBookings.push(...resolvedBookings);
      }

      setBookings(allBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateHours = (booking: Booking): number => {
    const start = booking.startTime.toDate();
    const end = booking.endTime.toDate();
    return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  };

  const getStatsByVenue = () => {
    const stats: { [venueId: string]: { count: number; hours: number; revenue: number; venueName: string; pricePerHour: number } } = {};

    venues.forEach(venue => {
      stats[venue.id] = {
        count: 0,
        hours: 0,
        revenue: 0,
        venueName: venue.name,
        pricePerHour: venue.pricePerHour
      };
    });

    bookings.forEach(booking => {
      if (stats[booking.venueId]) {
        const hours = calculateHours(booking);
        const venue = venues.find(v => v.id === booking.venueId);
        const pricePerHour = venue?.pricePerHour || 2500;

        stats[booking.venueId].count++;
        stats[booking.venueId].hours += hours;
        stats[booking.venueId].revenue += hours * pricePerHour;
      }
    });

    return stats;
  };

  const getTotalStats = () => {
    const venueStats = getStatsByVenue();
    let totalBookings = 0;
    let totalHours = 0;
    let totalRevenue = 0;

    Object.values(venueStats).forEach(stat => {
      totalBookings += stat.count;
      totalHours += stat.hours;
      totalRevenue += stat.revenue;
    });

    return { totalBookings, totalHours, totalRevenue };
  };

  const getTopPlayers = () => {
    const playerStats: { [userId: string]: { name: string; hours: number; bookings: number } } = {};

    bookings.forEach(booking => {
      const hours = calculateHours(booking);

      if (!playerStats[booking.userId]) {
        playerStats[booking.userId] = {
          name: booking.userName || 'Ismeretlen felhasználó',
          hours: 0,
          bookings: 0
        };
      }

      playerStats[booking.userId].hours += hours;
      playerStats[booking.userId].bookings++;
    });

    return Object.entries(playerStats)
      .map(([userId, stats]) => ({ userId, ...stats }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 5);
  };

  const getPeakHours = () => {
    const hourCounts: { [hour: number]: number } = {};

    bookings.forEach(booking => {
      const startHour = booking.startTime.toDate().getHours();
      hourCounts[startHour] = (hourCounts[startHour] || 0) + 1;
    });

    return Object.entries(hourCounts)
      .map(([hour, count]) => ({ hour: parseInt(hour), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const getRecentBookings = () => {
    return [...bookings]
      .sort((a, b) => b.startTime.toDate().getTime() - a.startTime.toDate().getTime())
      .slice(0, 5)
      .map(booking => {
        const venue = venues.find(v => v.id === booking.venueId);
        return {
          ...booking,
          venueName: venue?.name || 'Ismeretlen helyszín'
        };
      });
  };

  const venueStats = getStatsByVenue();
  const totalStats = getTotalStats();
  const topPlayers = getTopPlayers();
  const peakHours = getPeakHours();
  const recentBookings = getRecentBookings();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={() => setTimeRange('daily')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${timeRange === 'daily'
            ? 'bg-[#00AFF2] text-white shadow-lg'
            : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
            }`}
        >
          Napi
        </button>
        <button
          onClick={() => setTimeRange('weekly')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${timeRange === 'weekly'
            ? 'bg-[#00AFF2] text-white shadow-lg'
            : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
            }`}
        >
          Heti
        </button>
        <button
          onClick={() => setTimeRange('monthly')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${timeRange === 'monthly'
            ? 'bg-[#00AFF2] text-white shadow-lg'
            : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
            }`}
        >
          Havi
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-[#00AFF2]/10 rounded-lg">
              <svg className="w-8 h-8 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <p className="text-sm text-[#B8B8C3] mb-1">Összes foglalás</p>
          <p className="text-3xl font-bold text-white">{totalStats.totalBookings}</p>
          <p className="text-sm text-[#00AFF2] mt-2">{totalStats.totalHours.toFixed(1)} óra</p>
        </div>

        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-sm text-[#B8B8C3] mb-1">Bevétel</p>
          <p className="text-3xl font-bold text-green-400">{totalStats.totalRevenue.toLocaleString()} Ft</p>
          <p className="text-sm text-[#B8B8C3] mt-2">{totalStats.totalHours.toFixed(1)} óra × ár</p>
        </div>

        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <p className="text-sm text-[#B8B8C3] mb-1">Helyszínek</p>
          <p className="text-3xl font-bold text-purple-400">{venues.length}</p>
          <p className="text-sm text-[#B8B8C3] mt-2">Aktív helyszínek</p>
        </div>
      </div>

      {/* Venue Breakdown */}
      <div className="space-y-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <span className="text-2xl">📊</span>
          Helyszínenkénti bontás
        </h3>
        {Object.entries(venueStats).map(([venueId, stats]) => (
          <div key={venueId} className="card-dark p-6">
            <h4 className="text-lg font-bold text-white mb-4">{stats.venueName}</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-[#B8B8C3] mb-1">Órák</p>
                <p className="text-2xl font-bold text-[#00AFF2]">{stats.hours.toFixed(1)}</p>
                <p className="text-xs text-[#B8B8C3] mt-1">{stats.count} foglalás</p>
              </div>
              <div>
                <p className="text-xs text-[#B8B8C3] mb-1">Bevétel</p>
                <p className="text-2xl font-bold text-green-400">{stats.revenue.toLocaleString()} Ft</p>
                <p className="text-xs text-[#B8B8C3] mt-1">{stats.pricePerHour} Ft/óra</p>
              </div>
              <div>
                <p className="text-xs text-[#B8B8C3] mb-1">Foglalások</p>
                <p className="text-2xl font-bold text-white">{stats.count}</p>
                <p className="text-xs text-[#B8B8C3] mt-1">db</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Total Summary */}
      <div className="card-dark p-6">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <span className="text-2xl">💰</span>
          Összesen
        </h3>
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-sm text-[#B8B8C3] mb-2">foglalás</p>
            <p className="text-4xl font-bold text-white">{totalStats.totalBookings}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-[#B8B8C3] mb-2">óra</p>
            <p className="text-4xl font-bold text-[#00AFF2]">{totalStats.totalHours.toFixed(1)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-[#B8B8C3] mb-2">Bevétel</p>
            <p className="text-4xl font-bold text-green-400">{totalStats.totalRevenue.toLocaleString()} Ft</p>
          </div>
        </div>
      </div>

      {/* Top Players */}
      {topPlayers.length > 0 && (
        <div className="card-dark p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <span className="text-2xl">🏆</span>
            Top játékosok
            <span className="text-sm text-[#B8B8C3] font-normal ml-2">(Legtöbbet foglaló játékosok)</span>
          </h3>
          <div className="space-y-3">
            {topPlayers.map((player, index) => {
              const isTop3 = index < 3;
              const medals = ['🥇', '🥈', '🥉'];
              const gradients = [
                'bg-gradient-to-r from-yellow-600/20 to-yellow-400/20 border-yellow-400/50',
                'bg-gradient-to-r from-gray-400/20 to-gray-300/20 border-gray-300/50',
                'bg-gradient-to-r from-orange-600/20 to-orange-400/20 border-orange-400/50'
              ];
              const textColors = ['text-yellow-400', 'text-gray-300', 'text-orange-400'];

              return (
                <div
                  key={player.userId}
                  className={`p-4 rounded-lg border-2 ${isTop3 ? gradients[index] + ' shadow-lg' : 'bg-[#15191E] border-[#31383D]'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`text-3xl ${isTop3 ? '' : 'text-[#B8B8C3]'}`}>
                        {isTop3 ? medals[index] : `#${index + 1}`}
                      </div>
                      <div>
                        <p className="text-lg font-bold text-white">{player.name}</p>
                        <p className="text-sm text-[#B8B8C3]">{player.bookings} foglalás</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-3xl font-bold ${isTop3 ? textColors[index] : 'text-white'}`}>
                        {player.hours.toFixed(1)}
                      </p>
                      <p className="text-sm text-[#B8B8C3]">óra</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Peak Hours and Recent Bookings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Peak Hours */}
        {peakHours.length > 0 && (
          <div className="card-dark p-6">
            <h3 className="text-xl font-bold text-white mb-6">Csúcsidőszakok</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Legforgalmasabb Időszak</p>
                  <p className="text-[#B8B8C3] text-sm">Legtöbb foglalás ebben az időszakban</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#00AFF2]">
                    {peakHours[0]?.hour}:00 - {peakHours[0]?.hour + 1}:00
                  </p>
                  <p className="text-sm text-[#B8B8C3]">{peakHours[0]?.count} foglalás</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Aktív Helyszínek</p>
                  <p className="text-[#B8B8C3] text-sm">Működő venue-k száma</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#2bc48a]">{venues.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Bookings */}
        {recentBookings.length > 0 && (
          <div className="card-dark p-6">
            <h3 className="text-xl font-bold text-white mb-6">Legutóbbi Foglalások</h3>
            <div className="space-y-3">
              {recentBookings.map((booking) => (
                <div key={booking.id} className="p-3 bg-[#15191E] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-medium">{booking.userName}</p>
                    <p className="text-sm text-[#B8B8C3]">
                      {booking.startTime.toDate().toLocaleDateString('hu-HU', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#B8B8C3]">{booking.venueName}</p>
                    <p className="text-sm text-[#00AFF2]">
                      {calculateHours(booking).toFixed(1)} óra
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueBookingStatistics;
